import React, { useState, Fragment }  from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { DateTime } from 'luxon';
import NumberFormat from 'react-number-format';

import Link from '@material-ui/core/Link';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import Button from '@material-ui/core/Button';

import IndustryTextField from 'app/components/textfields/IndustryTextField';

import States from 'app/utils/States';
import { FormatEIN, FormatSocialLastFour } from 'app/utils/FormatTaxId';

import useApp from 'app/context/useApp';
import useApi from 'app/context/useApi';

const days = [1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26,27,28,29,30,31];
const months = [1,2,3,4,5,6,7,8,9,10,11,12];

const useStyles = makeStyles(theme => ({
    section: {
        marginBottom: theme.spacing(3)
    },
    copyLink: {
        cursor: 'pointer',
        marginLeft: theme.spacing(1)
    }
}));

export default function Step1(props) {

    const { app } = useApp();

    const { setLoading, refreshClub } = props;

    const [ values, setValues ] = useState({
        email: app.club.support_email,
        business_type: 'non_profit',

        //personal
        first: app.user.first,
        last: app.user.last,
        phone: app.user.phone_mobile || "",
        day: 1,
        month: 1,
        year: DateTime.local().minus({years: 30}).toFormat('yyyy'),
        ssn_last_4: "",
        address_one: app.user.address_one || "",
        address_two: app.user.address_two || "",
        city: app.user.city || "",
        state: app.user.state || "",
        zip: app.user.zip || "",

        // business
        name: app.club.legal_name || app.club.name,
        title: "Director",
        business_phone: app.user.business_phone || "",
        ein: "",
        mcc: "",
        business_address_one: app.club.business_address_one || "",
        business_address_two: app.club.business_address_two || "",
        business_city: app.club.business_city || "",
        business_state: app.club.business_state || "",
        business_zip: app.club.business_zip || "",
    });

    const [ errors, setErrors ] = useState({})

    const classes = useStyles();
    const api = useApi();

    // Setters -------------------------------------------------

    const handleChange = name => e => {

        const target = e.target;
        const value = (target.type === 'checkbox' ? target.checked : target.value);

        if (isError(name)) delete errors[name];

        setValues({ ...values, [name]: value });
    };

    const handleEin = e => {

        const value = e.target.value;

        if (isError('ein')) delete errors.ein;

        setValues({ ...values, ein: FormatEIN(value) });
    };

    const handleSocialLastFour = e => {

        const value = e.target.value;

        if (isError('ssn_last_4')) delete errors.ssn_last_4;

        setValues({ ...values, ssn_last_4: FormatSocialLastFour(value) });
    };

    const handleMcc = (e, dataSet) => {
        const value = (dataSet !== null) ? dataSet.value : "";

        if (isError('mcc')) delete errors.mcc;

        setValues({ ...values, mcc: value });
    }

    const copyPersonalAddress = () => {

        if (isError('business_address_one')) delete errors.business_address_one;
        if (isError('business_address_two')) delete errors.business_address_two;
        if (isError('business_city')) delete errors.business_city;
        if (isError('business_state')) delete errors.business_state;
        if (isError('business_zip')) delete errors.business_zip;

        setValues({
            ...values,
            business_address_one: values.address_one,
            business_address_two: values.address_two,
            business_city: values.city,
            business_state: values.state,
            business_zip: values.zip
        })
    }

    // Posters -------------------------------------------------

    const postAccount = () => {

        setLoading(true);

        api.post('/admin/accounting/stripe/create', values)
            .then(res => refreshClub())
            .catch(error => {
                setLoading(false)

                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            });
    }

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    }

    return (
        <form>
            <Grid container justify="space-between" align="center" className={classes.section}>
                <Grid item>
                    <Typography variant="h6" component="h2">
                        <Typography variant="inherit" component="span" color="primary">
                            Step 1:&nbsp;
                        </Typography>
                        <Typography variant="inherit" component="span">
                            Account Setup
                        </Typography>
                    </Typography>
                </Grid>
                <Grid item>

                </Grid>
            </Grid>
            <Typography variant="subtitle1" color="textSecondary" className={classes.section}>
                To begin accepting payments, we need to build your personal/business profile to protect against potential fraud. All information is kept private and secure and will be trasferred between Club Apps and our payment processor via secured channels. Please note: <Typography variant="inherit" color="error" component="span">We only support entities located in the <strong>United States</strong> at this time.</Typography>
            </Typography>
            <Grid container spacing={3}>
                <Grid item xs={6}>
                    <Typography variant="subtitle2">General Information:</Typography>
                </Grid>
                <Grid item xs={6}>

                </Grid>
                <Grid item xs={6}>
                    <TextField
                        id="input-payment-onboarding-email"
                        label="Support Email"
                        name="email"
                        value={values.email}
                        onChange={handleChange('email')}
                        fullWidth
                        error={isError('email')}
                        helperText={getErrorMessage('email')}
                        variant="outlined"
                    />
                </Grid>
                <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                        This is the email that will be registered with our payment processor.
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        id="input-payment-onboarding-business-type"
                        select
                        label="Select Business Type"
                        value={values.business_type}
                        onChange={handleChange('business_type')}
                        error={isError('business_type')}
                        helperText={getErrorMessage('business_type')}
                        variant="outlined"
                        fullWidth
                        >
                        <MenuItem value="individual">
                            Individual
                        </MenuItem>
                        <MenuItem value="company">
                            Company
                        </MenuItem>
                        <MenuItem value="non_profit">
                            Non Profit
                        </MenuItem>
                        <MenuItem value="government_entity">
                            Government Entity
                        </MenuItem>
                    </TextField>
                </Grid>
                <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                        Please select the appropriate business type. Changing this can greatly affect the time in which you receive payouts.
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    <IndustryTextField
                        value={values.mcc}
                        onChange={handleMcc}
                        groupBy={'club_cat'}
                        error={isError('mcc')}
                        helperText={getErrorMessage('mcc')}
                    />
                </Grid>
                <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                        Select the industry that most closely represents your organization and it's functions.
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <Typography variant="subtitle2">
                        Personal Information:
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                        Legal Name:
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        id="input-payment-onboarding-first"
                        label="First"
                        value={values.first}
                        onChange={handleChange('first')}
                        fullWidth
                        variant='outlined'
                        error={isError('first')}
                        helperText={getErrorMessage('first')}
                    />
                </Grid>
                <Grid item xs={6}>

                </Grid>
                <Grid item xs={6}>
                    <TextField
                        id="input-payment-onboarding-last"
                        label="Last"
                        value={values.last}
                        onChange={handleChange('last')}
                        fullWidth
                        variant='outlined'
                        error={isError('last')}
                        helperText={getErrorMessage('last')}
                    />
                </Grid>
                <Grid item xs={6}>

                </Grid>
                <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                        Phone Number
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    <NumberFormat
                        format="(###) ###-####"
                        allowEmptyFormatting
                        customInput={TextField}
                        id="input-payment-onboarding-phone"
                        type="tel"
                        label="Phone"
                        value={values.phone}
                        onChange={handleChange('phone')}
                        fullWidth
                        variant='outlined'
                        error={isError('phone')}
                        helperText={getErrorMessage('phone')}
                    />
                </Grid>
                <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                        Club Apps and/or our payment processor may use this phone number to help verify your account.
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                        Last 4 of Social
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        id="input-payment-onboarding-ssn-last-4"
                        label="SSN Last 4"
                        value={values.ssn_last_4}
                        onChange={handleSocialLastFour}
                        fullWidth
                        variant='outlined'
                        error={isError('ssn_last_4')}
                        helperText={getErrorMessage('ssn_last_4')}
                        placeholder="XXX-XX-"
                        />
                </Grid>
                <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                        We use the last four digits of your social security number to verfiy your identity. This information is not stored on our servers.
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                        Date of Birth:
                    </Typography>
                </Grid>
                <Grid item xs={2}>
                    <TextField
                        id="input-payment-onboarding-dob-month"
                        label="Month"
                        value={values.month}
                        onChange={handleChange('month')}
                        fullWidth
                        error={isError('month')}
                        helperText={getErrorMessage('month')}
                        variant="outlined"
                        select
                        >
                        {months.map( month => (
                            <MenuItem key={month} value={month}>{month}</MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid item xs={2}>
                    <TextField
                        id="input-payment-onboarding-dob-day"
                        label="Day"
                        value={values.day}
                        onChange={handleChange('day')}
                        fullWidth
                        error={isError('day')}
                        helperText={getErrorMessage('day')}
                        variant="outlined"
                        select
                        >
                        {days.map( day => (
                            <MenuItem key={day} value={day}>{day}</MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid item xs={2}>
                    <TextField
                        id="input-payment-onboarding-dob-year"
                        label="Year"
                        value={values.year}
                        onChange={handleChange('year')}
                        fullWidth
                        error={isError('year')}
                        helperText={getErrorMessage('year')}
                        variant="outlined"
                        maxLength="4"
                    />
                </Grid>
                <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                        Like the last four of your social, we use this information to verfiy your identity. This information is not stored on our servers.
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                        Personal Address:
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        id="input-payment-onboarding-address-one"
                        label="Address One"
                        value={values.address_one}
                        onChange={handleChange('address_one')}
                        fullWidth
                        error={isError('address_one')}
                        helperText={getErrorMessage('address_one')}
                        variant="outlined"
                        autoComplete="personal-address-line1"
                    />
                </Grid>
                <Grid item xs={6}>

                </Grid>
                <Grid item xs={6}>
                    <TextField
                        id="input-payment-onboarding-address-two"
                        label="Address Two"
                        value={values.address_two}
                        onChange={handleChange('address_two')}
                        fullWidth
                        error={isError('address_two')}
                        helperText={getErrorMessage('address_two')}
                        variant="outlined"
                        autoComplete="personal-address-line2"
                    />
                </Grid>
                <Grid item xs={6}>

                </Grid>
                <Grid item xs={6}>
                    <TextField
                        id="input-payment-onboarding-city"
                        label="City"
                        value={values.city}
                        onChange={handleChange('city')}
                        fullWidth
                        variant='outlined'
                        error={isError('city')}
                        helperText={getErrorMessage('city')}
                        autoComplete="personal-address-level2"
                        />
                </Grid>
                <Grid item xs={6}>

                </Grid>
                <Grid item xs={6}>
                    <TextField
                        id="input-payment-onboarding-state"
                        select
                        label="State"
                        value={values.state}
                        onChange={handleChange('state')}
                        fullWidth
                        variant='outlined'
                        error={isError('state')}
                        helperText={getErrorMessage('state')}
                        autoComplete="personal-address-level1"
                        >
                        {States.map( state => (
                            <MenuItem key={state.abbreviation} value={state.abbreviation}>{state.name}</MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid item xs={6}>

                </Grid>
                <Grid item xs={6}>
                    <TextField
                        id="input-payment-onboarding-zip"
                        label="ZIP"
                        value={values.zip}
                        onChange={handleChange('zip')}
                        fullWidth
                        variant='outlined'
                        error={isError('zip')}
                        helperText={getErrorMessage('zip')}
                        autoComplete="personal-address-postal-code"
                        />
                </Grid>
                <Grid item xs={6}>

                </Grid>
                {values.business_type !== 'individual' &&
                    <Fragment>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2">
                                Business Information:
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2" color="textSecondary">
                                Business Legal Name
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                id="input-payment-onboarding-legal-name"
                                label="Legal Name"
                                name="legal-name"
                                value={values.name}
                                onChange={handleChange('name')}
                                fullWidth
                                error={isError('name')}
                                helperText={getErrorMessage()}
                                variant="outlined"

                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" color="textSecondary">
                                This is your entities registered legal name.
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2" color="textSecondary">
                                Your Position:
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                id="input-payment-onboarding-title"
                                label="Title"
                                value={values.title}
                                onChange={handleChange('title')}
                                fullWidth
                                variant='outlined'
                                error={isError('title')}
                                helperText={getErrorMessage('title')}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" color="textSecondary">
                                This is your position at {app.club.name}. We will use it to validate your ability to authorize payments and payouts.
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2" color="textSecondary">
                                Business Tax ID (EIN)
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                id="input-payment-onboarding-ein"
                                label="EIN"
                                value={values.ein}
                                onChange={handleEin}
                                fullWidth
                                variant='outlined'
                                error={isError('ein')}
                                helperText={getErrorMessage('ein')}
                                />
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" color="textSecondary">
                                We use your Tax ID (Typically your EIN) to help verfiy your business identity. This information is not stored on our servers.
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2" color="textSecondary">
                                Business Phone Number
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <NumberFormat
                                format="(###) ###-####"
                                allowEmptyFormatting
                                customInput={TextField}
                                id="input-payment-onboarding-business-phone"
                                type="tel"
                                label="Business Phone"
                                value={values.business_phone}
                                onChange={handleChange('business_phone')}
                                fullWidth
                                variant='outlined'
                                error={isError('business_phone')}
                                helperText={getErrorMessage('business_phone')}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" color="textSecondary">
                                Club Apps and/or our payment processor may use this phone number to help verify your account.
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2" color="textSecondary">
                                <span>Business Address:</span>
                                <Link
                                    className={classes.copyLink}
                                    onClick={copyPersonalAddress}
                                    >
                                    Copy Personal Address
                                </Link>
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                id="input-payment-onboarding-business-address-one"
                                label="Business Address One"
                                value={values.business_address_one}
                                onChange={handleChange('business_address_one')}
                                fullWidth
                                error={isError('business_address_one')}
                                helperText={getErrorMessage('business_address_one')}
                                variant="outlined"
                                autoComplete="business-address-line1"
                            />
                        </Grid>
                        <Grid item xs={6}>

                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                id="input-payment-onboarding-business-address-two"
                                label="Address Two"
                                value={values.business_address_two}
                                onChange={handleChange('business_address_two')}
                                fullWidth
                                error={isError('business_address_two')}
                                helperText={getErrorMessage('business_address_two')}
                                variant="outlined"
                                autoComplete="business-address-line2"
                            />
                        </Grid>
                        <Grid item xs={6}>

                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                id="input-payment-onboarding-business-city"
                                label="City"
                                value={values.business_city}
                                onChange={handleChange('business_city')}
                                fullWidth
                                variant='outlined'
                                error={isError('business_city')}
                                helperText={getErrorMessage('business_city')}
                                autoComplete="business-address-level2"
                                />
                        </Grid>
                        <Grid item xs={6}>

                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                id="input-payment-onboarding-business-state"
                                select
                                label="State"
                                value={values.business_state}
                                onChange={handleChange('business_state')}
                                fullWidth
                                variant='outlined'
                                error={isError('business_state')}
                                helperText={getErrorMessage('business_state')}
                                autoComplete="business-address-level1"
                                >
                                {States.map( state => (
                                    <MenuItem key={state.abbreviation} value={state.abbreviation}>{state.name}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>

                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                id="input-payment-onboarding-business-zip"
                                label="ZIP"
                                value={values.business_zip}
                                onChange={handleChange('business_zip')}
                                fullWidth
                                variant='outlined'
                                error={isError('business_zip')}
                                helperText={getErrorMessage('business_zip')}
                                autoComplete="business-postal-code"
                                />
                        </Grid>
                        <Grid item xs={6}>

                        </Grid>
                    </Fragment>
                }
                <Grid item xs={8}>
                    <Typography variant="inherit" color="textSecondary">
                        By clicking <strong>CREATE ACCOUNT</strong> and registering your new account with our payment processor, you agree to the <Link href="https://stripe.com/connect-account/legal" target="_blank">Stripe Connected Account Agreement</Link>.
                    </Typography>
                </Grid>
                <Grid item xs={4} align="right">
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={postAccount}
                        >
                        Create Account
                    </Button>
                </Grid>
            </Grid>
        </form>

    )
}
